import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { HighlightPopover } from '@omsimos/react-highlight-popover';
import PodcastPlayer from '../../components/PodcastPlayer/PodcastPlayer';
import DictionaryPanel from '../../components/DictionaryPanel';
import './ReadPodcast.css';


function splitSentences(text) {
    return String(text || '').split(/(?<=[.!?])\s+|\n+/).map(s => s.trim()).filter(Boolean);
}

function cleanWordText(text) {
    return String(text || '').toLowerCase().replace(/[^\p{L}\p{N}'-]/gu, '');
}

function buildSentenceTimings(sentences, timingData, duration) {
    const totalWords = sentences.map(s => s.split(/\s+/).filter(Boolean).length);
    const segments = Array.isArray(timingData) ? timingData :
        Array.isArray(timingData?.segments) ? timingData.segments :
        Array.isArray(timingData?.sentences) ? timingData.sentences :
        Array.isArray(timingData?.words) ? timingData.words : [];

    if (segments.length && (timingData?.segments || timingData?.sentences || (!timingData?.words && segments.some(x => x.text)))) {
        const hasSentenceSegments = segments.length <= sentences.length * 2;
        if (hasSentenceSegments) {
            return sentences.map((_, i) => {
                const seg = segments[i];
                return seg ? { start: Number(seg.start) || 0, end: Number(seg.end) || 0 } : null;
            });
        }
    }

    if (segments.length && timingData?.words) {
        const words = segments.filter(x => Number.isFinite(Number(x.start)) && Number.isFinite(Number(x.end)));
        const result = [];
        let cursor = 0;
        sentences.forEach(count => {
            const slice = words.slice(cursor, cursor + count);
            result.push(slice.length ? { start: Number(slice[0].start), end: Number(slice[slice.length - 1].end) } : null);
            cursor += count;
        });
        if (result.filter(Boolean).length >= Math.max(1, sentences.length * 0.6)) return result;
    }

    if (duration > 0) {
        const total = totalWords.reduce((a,b) => a+b, 0) || sentences.length;
        let cursor = 0;
        return totalWords.map(count => {
            const start = duration * cursor / total;
            cursor += count || 1;
            return { start, end: duration * cursor / total };
        });
    }
    return [];
}


const getGoogleDriveEmbedUrl = (url) => {
    const value = String(url || '').trim();
    const file = value.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
    if (file?.[1]) return `https://drive.google.com/file/d/${file[1]}/preview`;
    const id = value.match(/[?&]id=([^&]+)/i);
    if (id?.[1] && value.includes('drive.google.com')) return `https://drive.google.com/file/d/${id[1]}/preview`;
    return value;
};

function SynchronizedPodcastText({ content, currentTime, duration, timingUrl, audioUrl, onSeek, onWordSelect, followSentence = true }) {
    const sentences = useMemo(() => splitSentences(content), [content]);
    const [timings, setTimings] = useState([]);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!sentences.length) { setTimings([]); return; }
            let data = null;
            const candidates = [];
            if (timingUrl) candidates.push(timingUrl);
            if (timingUrl && !timingUrl.endsWith('.json')) candidates.push(`${timingUrl}.json`);
            if (audioUrl) candidates.push(audioUrl.replace(/\.[^/.?#]+([?#].*)?$/, '') + '.json', audioUrl + '.json');
            for (const url of candidates.filter(Boolean)) {
                try {
                    const value = String(url || '').trim();
                    if (value.startsWith('{') || value.startsWith('[')) { data = JSON.parse(value); break; }
                    const r = await fetch(url, { cache: 'no-store' });
                    if (r.ok) { data = await r.json(); break; }
                } catch {}
            }
            const built = buildSentenceTimings(sentences, data, duration);
            if (!cancelled) setTimings(built);
        }
        load();
        return () => { cancelled = true; };
    }, [sentences, timingUrl, duration]);

    const active = timings.findIndex(t => t && currentTime >= t.start && currentTime <= t.end);
    useEffect(() => {
        if (!followSentence || active < 0) return;
        const id = window.setTimeout(() => {
            document.querySelector('.episode-sync-text .sync-sentence-active')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 40);
        return () => window.clearTimeout(id);
    }, [active, followSentence]);
    return (
        <div className="episode-sync-text">
            {sentences.map((sentence, i) => (
                <span key={i} className={`sync-sentence ${active === i ? 'sync-sentence-active' : ''}`}
                    onClick={() => timings[i] && onSeek?.(timings[i].start)}>
                    {String(sentence).split(/(\s+)/).map((part, wi) => /\s+/.test(part) ? part : (
                        <button key={wi} type="button" className="podcast-sync-word" onClick={(e) => { e.stopPropagation(); onWordSelect?.(part.replace(/[^\p{L}\p{N}'-]/gu, '')); }}>
                            {part}
                        </button>
                    ))}{i < sentences.length - 1 ? ' ' : ''}
                </span>
            ))}
        </div>
    );
}

function ReadPodcast() {
    const { slug } = useParams();
    const [episodes, setEpisodes] = useState([]);
    const [selectedEpisode, setSelectedEpisode] = useState(null);
    const [content, setContent] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const [editAudioLink, setEditAudioLink] = useState('');
    const [editTimingLink, setEditTimingLink] = useState('');
    const [showEpisodeForm, setShowEpisodeForm] = useState(false);
    const [newEpisode, setNewEpisode] = useState({ episodeNumber: '', title: '', content: '', order: '', audioLink: '', timingLink: '' });
    const [podcastTitle, setPodcastTitle] = useState('');
    const [podcastId, setPodcastId] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [audioKey, setAudioKey] = useState(Date.now());
    const [podcastTime, setPodcastTime] = useState(0);
    const [podcastDuration, setPodcastDuration] = useState(0);
    const [podcastSeekRequest, setPodcastSeekRequest] = useState(null);
    const [dictionaryWord, setDictionaryWord] = useState('');
    const [dictionaryMobileOpen, setDictionaryMobileOpen] = useState(false);
    const [followSentence, setFollowSentence] = useState(true);
    
    const [popoverSelection, setPopoverSelection] = useState('');
    const [popoverMeaning, setPopoverMeaning] = useState('');
    const [isPopoverTranslating, setIsPopoverTranslating] = useState(false);

    useEffect(() => {
        if (!popoverSelection) return;
        
        const translate = async () => {
            setIsPopoverTranslating(true);
            const translated = await translateText(popoverSelection);
            setPopoverMeaning(translated);
            setIsPopoverTranslating(false);
        };
        translate();
    }, [popoverSelection]);

    const convertToEnglishNumbers = (text) => {
        if (!text) return '';
        const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        
        let result = text;
        for (let i = 0; i < persianNumbers.length; i++) {
            result = result.split(persianNumbers[i]).join(englishNumbers[i]);
        }
        return result;
    };

    const translateText = async (text) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/translate`, {
                params: { text: text, source: 'en', target: 'fa' }
            });
            return response.data.translatedText;
        } catch (error) {
            return "⚠️ ترجمه در دسترس نیست";
        }
    };

    useEffect(() => {
        const userData = localStorage.getItem('currentUser');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setCurrentUser(user);
                setIsAdmin(user.role === 'admin');
                
                if (user.role !== 'admin') {
                    checkUserSubscription(user._id);
                } else {
                    setHasSubscription(true);
                    setLoading(false);
                }
            } catch (e) {
                console.error("Error parsing user:", e);
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);

    const checkUserSubscription = async (userId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/check-subscription/${userId}`);
            setHasSubscription(response.data.hasActiveSubscription);
        } catch (error) {
            console.error("Error checking subscription:", error);
            setHasSubscription(false);
        } finally {
            setLoading(false);
        }
    };

    // دریافت اطلاعات پادکست
    useEffect(() => {
        const fetchPodcast = async () => {
            try {
                let response;
                if (slug.match(/^[0-9a-fA-F]{24}$/)) {
                    response = await axios.get(`${process.env.REACT_APP_API_URL}/podcast/${slug}`);
                } else {
                    response = await axios.get(`${process.env.REACT_APP_API_URL}/podcast/slug/${slug}`);
                }
                const podcast = response.data;
                setPodcastTitle(podcast.title || 'پادکست');
                setPodcastId(podcast._id);
            } catch (error) {
                console.error("Error fetching podcast:", error);
                setPodcastTitle('پادکست');
            }
        };
        if (slug) {
            fetchPodcast();
        } else {
            // اگر slug وجود نداشت، پادکست پیش‌فرض را دریافت کنید
            fetchDefaultPodcast();
        }
    }, [slug]);

    const fetchDefaultPodcast = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/podcast/default`);
            const podcast = response.data;
            setPodcastTitle(podcast.title || 'پادکست');
            setPodcastId(podcast._id);
        } catch (error) {
            console.error("Error fetching default podcast:", error);
            setPodcastTitle('پادکست');
        }
    };

    const fetchEpisodes = async () => {
        if (!podcastId) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/podcast-episodes/${podcastId}`);
            console.log('📥 قسمت‌های دریافت شده:', response.data.map(e => ({ 
                episodeNumber: e.episodeNumber, 
                audioLink: e.audioLink 
            })));
            setEpisodes(response.data);
            if (response.data.length > 0 && !selectedEpisode) {
                const firstEpisode = response.data[0];
                setSelectedEpisode(firstEpisode);
                setContent(firstEpisode.content || '');
                setEditContent(firstEpisode.content || '');
                setEditTitle(firstEpisode.title || '');
                setEditAudioLink(firstEpisode.audioLink || '');
                setEditTimingLink(firstEpisode.timingLink || '');
                setPodcastTime(0); setPodcastDuration(0); setPodcastSeekRequest(null);
                setAudioKey(Date.now());
            }
        } catch (error) {
            console.error("Error fetching episodes:", error);
        }
    };

    useEffect(() => {
        if (podcastId) {
            fetchEpisodes();
        }
    }, [podcastId]);

    const handleEpisodeClick = (episode, index) => {
        if (!isAdmin && !hasSubscription && index > 0) {
            alert('برای دسترسی به این بخش، لطفاً اشتراک تهیه کنید.');
            window.location.href = '/subscription';
            return;
        }
        
        console.log('🎵 کلیک روی قسمت:', episode.episodeNumber, 'لینک صوتی:', episode.audioLink);
        
        setSelectedEpisode(episode);
        setContent(episode.content || '');
        setEditContent(episode.content || '');
        setEditTitle(episode.title || '');
        setEditAudioLink(episode.audioLink || '');
        setEditTimingLink(episode.timingLink || '');
        setEditMode(false);
        setSidebarOpen(false);
        setPodcastTime(0); setPodcastDuration(0); setPodcastSeekRequest(null);
        setAudioKey(Date.now());
    };

    const handleEditClick = () => {
        if (!selectedEpisode) return;
        setEditMode(true);
        setEditTitle(selectedEpisode.title || '');
        setEditContent(selectedEpisode.content || '');
        setEditAudioLink(selectedEpisode.audioLink || '');
        setEditTimingLink(selectedEpisode.timingLink || '');
    };

    const handleSaveContent = async () => {
        if (!selectedEpisode) return;
        
        console.log('💾 ذخیره قسمت:', selectedEpisode.episodeNumber, 'لینک صوتی جدید:', editAudioLink);
        
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/podcast-episode/${selectedEpisode._id}`, {
                title: editTitle,
                content: editContent,
                audioLink: editAudioLink || null,
                timingLink: editTimingLink || null
            });
            
            console.log('✅ پاسخ سرور:', response.data);
            
            const updatedEpisode = { 
                ...selectedEpisode, 
                title: editTitle, 
                content: editContent,
                audioLink: editAudioLink || null,
                timingLink: editTimingLink || null
            };
            setSelectedEpisode(updatedEpisode);
            setContent(editContent);
            setAudioKey(Date.now());
            
            const updatedEpisodes = episodes.map(e => 
                e._id === selectedEpisode._id ? updatedEpisode : e
            );
            setEpisodes(updatedEpisodes);
            
            setEditMode(false);
            alert('✅ قسمت با موفقیت ذخیره شد!');
        } catch (error) {
            console.error("❌ خطا در ذخیره قسمت:", error);
            alert('خطا در ذخیره قسمت');
        }
    };

    const handleAddEpisode = async (e) => {
        e.preventDefault();
        if (!podcastId) return;
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/podcast-episode`, {
                podcastId: podcastId,
                episodeNumber: parseInt(newEpisode.episodeNumber),
                title: newEpisode.title,
                content: newEpisode.content,
                order: parseInt(newEpisode.order) || parseInt(newEpisode.episodeNumber),
                audioLink: newEpisode.audioLink || null,
                timingLink: newEpisode.timingLink || null
            });
            
            console.log('✅ قسمت جدید اضافه شد:', response.data);
            
            alert('قسمت با موفقیت اضافه شد!');
            setShowEpisodeForm(false);
            setNewEpisode({ episodeNumber: '', title: '', content: '', order: '', audioLink: '', timingLink: '' });
            await fetchEpisodes();
        } catch (error) {
            console.error("Error adding episode:", error);
            alert('خطا در افزودن قسمت');
        }
    };

    const handleDeleteEpisode = async (episodeId) => {
        if (window.confirm('آیا از حذف این قسمت مطمئن هستید؟')) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/podcast-episode/${episodeId}`);
                alert('قسمت با موفقیت حذف شد!');
                if (selectedEpisode?._id === episodeId) setSelectedEpisode(null);
                await fetchEpisodes();
            } catch (error) {
                console.error("Error deleting episode:", error);
                alert('خطا در حذف قسمت');
            }
        }
    };

    // ===== موقعیت قسمت فعلی در لیست، برای «پخش خودکار قسمت بعد» =====
    const currentEpisodeIndex = selectedEpisode
        ? episodes.findIndex((e) => e._id === selectedEpisode._id)
        : -1;
    const canAutoAdvance = isAdmin || hasSubscription;
    const hasNextEpisode =
        canAutoAdvance && currentEpisodeIndex >= 0 && currentEpisodeIndex < episodes.length - 1;

    const goToNextEpisode = () => {
        if (!hasNextEpisode) return;
        const next = episodes[currentEpisodeIndex + 1];
        handleEpisodeClick(next, currentEpisodeIndex + 1);
    };

    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('currentUser');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setCurrentUserId(user._id);
            } catch(e) {}
        }
    }, []);
    
    const handleSaveWord = async (word, meaning, context) => {
        if (!currentUserId) {
            alert('لطفاً ابتدا وارد شوید');
            window.location.href = '/login';
            return;
        }
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/save-word`, {
                userId: currentUserId,
                word: word,
                meaning: meaning,
                context: context,
                bookId: podcastId,
                bookTitle: podcastTitle
            });
            
            if (response.data.success) {
                alert('✅ کلمه ذخیره شد!');
            }
        } catch (error) {
            console.error(error);
            
            if (error.response?.data?.needSubscription) {
                if (window.confirm(
                    '⚠️ ذخیره کلمات فقط برای کاربران دارای اشتراک فعال امکان‌پذیر است.\n\n' +
                    'برای دسترسی به این قابلیت و فلش‌کارت، لطفاً اشتراک تهیه کنید.\n\n' +
                    'آیا می‌خواهید اشتراک تهیه کنید؟'
                )) {
                    window.location.href = '/subscription';
                }
            } else {
                alert(error.response?.data?.message || 'خطا در ذخیره کلمه');
            }
        }
    };

    const renderPopover = ({ selection, setShowPopover, ...props }) => {
        React.useEffect(() => {
            setPopoverSelection(selection);
        }, [selection]);
        
        const handleClose = () => {
            if (setShowPopover && typeof setShowPopover === 'function') {
                setShowPopover(false);
            } else {
                const popupElement = document.querySelector('.translation-popup');
                if (popupElement) {
                    popupElement.style.display = 'none';
                }
            }
        };
        
        const handleSave = () => {
            if (currentUserId) {
                handleSaveWord(selection, popoverMeaning, '');
            } else {
                alert('لطفاً ابتدا وارد شوید');
            }
            handleClose();
        };
        
        return (
            <div className="translation-popup" {...props}>
                <div className="selected-text">📖 {selection}</div>
                <div className="translation-text">
                    {isPopoverTranslating ? '🔄 در حال ترجمه...' : `📝 ${popoverMeaning}`}
                </div>
                <div className="popup-buttons">
                    <button 
                        className="save-word-btn"
                        onClick={handleSave}
                        disabled={isPopoverTranslating}
                    >
                        📥 ذخیره کلمه
                    </button>
                    <button 
                        className="close-popup" 
                        onClick={handleClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>
            </div>
        );
    };

    const handleDictionaryWord = (word) => {
        const clean = String(word || '').trim();
        if (!clean) return;
        setDictionaryWord(clean);
        if (window.innerWidth <= 768) setDictionaryMobileOpen(true);
    };

    const handleSelectionEnd = async (selection) => {
        if (selection && selection.length > 0) {
            const translated = await translateText(selection);
            setPopoverMeaning(translated);
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="loading-container">در حال بارگذاری...</div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="read-podcast-container">
                <div className={`episodes-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="episodes-header">
                        <h3>🎙️ episodes</h3>
                        {isAdmin && <button className="btn-add-episode" onClick={() => setShowEpisodeForm(!showEpisodeForm)}>+ افزودن قسمت</button>}
                    </div>

                    {showEpisodeForm && isAdmin && (
                        <form onSubmit={handleAddEpisode} className="add-episode-form">
                            <input type="number" placeholder="شماره قسمت *" value={newEpisode.episodeNumber} onChange={(e) => setNewEpisode({...newEpisode, episodeNumber: e.target.value})} required />
                            <input type="text" placeholder="عنوان قسمت (اختیاری)" value={newEpisode.title} onChange={(e) => setNewEpisode({...newEpisode, title: e.target.value})} />
                            <textarea placeholder="متن قسمت" rows="2" value={newEpisode.content} onChange={(e) => setNewEpisode({...newEpisode, content: e.target.value})} />
                            <input type="number" placeholder="ترتیب (اختیاری)" value={newEpisode.order} onChange={(e) => setNewEpisode({...newEpisode, order: e.target.value})} />
                            <input 
                                type="url" 
                                placeholder="🎵 لینک فایل صوتی (اختیاری)" 
                                value={newEpisode.audioLink || ''} 
                                onChange={(e) => setNewEpisode({...newEpisode, audioLink: e.target.value})} 
                                className="mt-2"
                            />
                            <button type="submit">ذخیره قسمت</button>
                            <button type="button" onClick={() => setShowEpisodeForm(false)}>انصراف</button>
                        </form>
                    )}

                    <ul className="episodes-list">
                        {episodes.map((episode, index) => {
                            const isLocked = !isAdmin && !hasSubscription && index > 0;
                            return (
                                <li key={episode._id} className={`episode-item ${selectedEpisode?._id === episode._id ? 'active' : ''} ${isLocked ? 'locked' : ''}`}>
                                    <span className="episode-link" onClick={() => handleEpisodeClick(episode, index)}>
                                        {isLocked && <span className="lock-icon">🔒 </span>}
                                         episode {episode.episodeNumber}: {episode.title || `قسمت ${episode.episodeNumber}`}
                                        {episode.audioLink && <span className="ms-1">🎵</span>}
                                    </span>
                                    {isAdmin && (
                                        <button className="btn-delete-episode" onClick={() => handleDeleteEpisode(episode._id)}>🗑️</button>
                                    )}
                                </li>
                            );
                        })}
                        {episodes.length === 0 && <li className="no-episodes">هنوز قسمتی اضافه نشده است. {isAdmin && 'برای افزودن کلیک کنید'}</li>}
                    </ul>
                    
                    {!isAdmin && !hasSubscription && episodes.length > 1 && (
                        <div className="upgrade-message">
                            <span>🔓 برای دسترسی به همه قسمت‌ها، </span>
                            <a href="/subscription">اشتراک تهیه کنید</a>
                        </div>
                    )}
                </div>

                <div className="episode-content-area">
                    {selectedEpisode ? (
                        <>
                            <div className="episode-content-header">
                                <button className="mobile-toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
                                
                                <HighlightPopover
                                    renderPopover={renderPopover}
                                    minSelectionLength={1}
                                    onSelectionEnd={handleSelectionEnd}
                                >
                                    <h2>
                                        episode {selectedEpisode.episodeNumber}: {selectedEpisode.title || `قسمت ${selectedEpisode.episodeNumber}`}
                                        {selectedEpisode.audioLink && <span className="ms-2">🎵</span>}
                                    </h2>
                                </HighlightPopover>
                                
                                {isAdmin && !editMode && (
                                    <button className="btn-edit-content" onClick={handleEditClick}>
                                        ✏️ ویرایش
                                    </button>
                                )}
                            </div>
                            <div className="episode-content-body">
                                {editMode ? (
                                    <>
                                        <input type="text" className="form-control mb-2" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="عنوان قسمت" />
                                        <textarea className="content-editor" value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={20} placeholder="متن قسمت را بنویسید..." />
                                        
                                        <div className="audio-link-input mt-3">
                                            <label className="form-label fw-bold">🎵 لینک فایل صوتی:</label>
                                            <input 
                                                type="url" 
                                                className="form-control" 
                                                value={editAudioLink || ''} 
                                                onChange={(e) => setEditAudioLink(e.target.value)}
                                                placeholder="https://example.com/audio.mp3"
                                            />
                                            {editAudioLink && (
                                                <div className="audio-player-container mt-2">
                                                    <audio controls className="w-100" key={`preview-${Date.now()}`}>
                                                        <source src={editAudioLink} type="audio/mpeg" />
                                                        مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
                                                    </audio>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-3">
                                            <label className="form-label fw-bold">⏱ تایم‌کد دقیق جمله‌ها:</label>
                                            <textarea className="form-control" value={editTimingLink || ''} onChange={(e) => setEditTimingLink(e.target.value)} rows={4} placeholder="لینک timing.json یا خود JSON را وارد کنید" />
                                            <small className="text-muted">فرمت: {`{ "words": [{ "text": "...", "start": 0.5, "end": 0.8 }] }`} یا segments با start/end</small>
                                        </div>
                                        
                                        <div className="edit-actions mt-3">
                                            <button onClick={handleSaveContent}>ذخیره</button>
                                            <button onClick={() => setEditMode(false)}>انصراف</button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* ====== پخش‌کننده صوتی حرفه‌ای در بالای متن ====== */}
                                        <div className="podcast-follow-toolbar">
                                            <button type="button" className={followSentence ? 'active' : ''} onClick={() => setFollowSentence(v => !v)}>
                                                {followSentence ? '🟨 دنبال‌کردن جمله: فعال' : '⬜ دنبال‌کردن جمله: خاموش'}
                                            </button>
                                        </div>

                                        {selectedEpisode?.audioLink && (
                                            <div className="audio-player-container mb-4">
                                                {selectedEpisode.audioLink.includes('drive.google.com') ? (
                                                    <>
                                                        <h6 className="fw-bold">🎵 پخش آنلاین</h6>
                                                        {/* لینک‌های گوگل‌درایو با iframe نمایش داده می‌شوند چون پخش‌کننده سفارشی روی آن‌ها کار نمی‌کند */}
                                                        <iframe
                                                            src={getGoogleDriveEmbedUrl(selectedEpisode.audioLink)}
                                                            width="100%"
                                                            height="100"
                                                            allow="autoplay"
                                                            title={`پخش قسمت ${selectedEpisode.episodeNumber}`}
                                                            frameBorder="0"
                                                            className="google-drive-player"
                                                            style={{ borderRadius: '8px' }}
                                                        />
                                                    </>
                                                ) : (
                                                    <PodcastPlayer
                                                        key={`player-${selectedEpisode._id}-${audioKey}`}
                                                        src={selectedEpisode.audioLink}
                                                        title={podcastTitle}
                                                        subtitle={`قسمت ${selectedEpisode.episodeNumber}: ${selectedEpisode.title || ''}`}
                                                        storageKey={selectedEpisode._id}
                                                        onEnded={goToNextEpisode}
                                                        hasNext={hasNextEpisode}
                                                        onTime={setPodcastTime}
                                                        onDuration={setPodcastDuration}
                                                        seekRequest={podcastSeekRequest}
                                                    />
                                                )}

                                                <div className="audio-error text-danger mt-2" style={{ display: 'none', fontSize: '0.9rem' }}>
                                                    ⚠️ خطا در پخش فایل صوتی. لطفاً فایل را به صورت مستقیم دانلود کنید.
                                                </div>
                                            </div>
                                        )}

                                        {/* ====== متن قسمت ====== */}
                                        <HighlightPopover renderPopover={renderPopover} minSelectionLength={2} onSelectionEnd={handleSelectionEnd}>
                                            <div className="episode-content-display" style={{ textAlign: 'justify' }} onMouseUp={() => { const selected = window.getSelection?.()?.toString().trim(); if (selected) handleDictionaryWord(selected.split(/\s+/)[0].replace(/[^\p{L}\p{N}'-]/gu, '')); }}>
                                                {content ? (
                                                    <SynchronizedPodcastText
                                                        content={convertToEnglishNumbers(content)}
                                                        currentTime={podcastTime}
                                                        duration={podcastDuration}
                                                        timingUrl={selectedEpisode.timingLink || selectedEpisode.transcriptUrl || ''}
                                                        audioUrl={selectedEpisode.audioLink}
                                                        onSeek={(time) => setPodcastSeekRequest({ time, id: Date.now() })}
                                                        onWordSelect={handleDictionaryWord}
                                                        followSentence={followSentence}
                                                    />
                                                ) : <p className="no-content">متن موجود نیست. {isAdmin && "برای افزودن روی 'ویرایش' کلیک کنید."}</p>}
                                            </div>
                                        </HighlightPopover>
                                    </>
                                )}
                            </div>
                        </>
                    ) : <div className="no-selection"><p>یک قسمت را انتخاب کنید تا گوش دهید.</p></div>}
                </div>
            </div>
            
            <DictionaryPanel
                selectedWord={dictionaryWord}
                apiBase={process.env.REACT_APP_API_URL}
                userId={currentUser?._id}
                bookId={selectedEpisode?._id}
                bookTitle={podcastTitle}
                isMobileOpen={dictionaryMobileOpen}
                onToggleMobile={() => setDictionaryMobileOpen(v => !v)}
            />
            {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
            <Footer />
        </>
    );
}

export default ReadPodcast;